import React, { ReactElement } from "react";
import { CircularProgress, makeStyles } from "@material-ui/core";
import ButtonBase from "@material-ui/core/Button";
import { ButtonBaseProps } from "@material-ui/core/ButtonBase/ButtonBase";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import { tesaBlue, tesaDarkRedError, tesaGrey1, tesaGrey5, tesaWhite } from "../../styles/colors";

export interface CustomButtonProps extends ButtonBaseProps {
  add?: boolean;
  disabled?: boolean;
  download?: boolean;
  endIcon?: ReactElement;
  fullWidth?: boolean;
  href?: string;
  inProgress?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  primary?: boolean;
  remove?: boolean;
  startIcon?: ReactElement;
  text?: string;
  noTextWrap?: boolean;
}

const useStyles = makeStyles({
  icon: {
    alignItems: "center",
    display: "flex",
    height: "20px",
    marginRight: ({ text }: CustomButtonProps) => (text ? "10px" : 0),

    "& .MuiSvgIcon-root": {
      height: "100%",
    },
  },
  spinner: { height: "20px !important", width: "20px !important", color: "grey", marginRight: "12px" },
  button: {
    height: 36,
  },
  fullWidth: {
    width: "100%",
  },
  remove: {
    backgroundColor: tesaWhite,
    border: `1px solid ${tesaBlue}`,
    color: tesaBlue,
    "&:hover": {
      color: tesaDarkRedError,
      borderColor: tesaDarkRedError,
    },
    "&:active": {
      color: tesaDarkRedError,
      borderColor: tesaDarkRedError,
    },
    "&:disabled": {
      borderColor: tesaGrey1,
      color: tesaGrey1,
    },
  },
  primary: {
    backgroundColor: tesaBlue,
    border: `1px solid ${tesaBlue}`,
    color: tesaWhite,
    "&:hover": {
      backgroundColor: tesaWhite,
      color: tesaBlue,
    },
    "&:active": {
      backgroundColor: tesaBlue,
      color: tesaWhite,
    },
    "&:disabled": {
      backgroundColor: tesaGrey5,
      border: `1px solid ${tesaGrey5}`,
      color: tesaGrey1,
    },
  },
  secondary: {
    backgroundColor: tesaWhite,
    border: `1px solid ${tesaBlue}`,
    color: tesaBlue,
    "&:hover": {
      backgroundColor: tesaBlue,
      color: tesaWhite,
    },
    "&:active": {
      backgroundColor: tesaWhite,
      color: tesaBlue,
    },
    "&:disabled": {
      backgroundColor: tesaWhite,
      border: `1px solid ${tesaGrey1}`,
      color: tesaGrey1,
    },
  },
});

const CustomButton: React.FC<CustomButtonProps> = (props) => {
  const classes = useStyles(props);
  const {
    add,
    className,
    disabled,
    href,
    download,
    endIcon,
    fullWidth,
    inProgress,
    noTextWrap,
    onClick,
    primary,
    remove,
    startIcon,
    style,
    text,
    type,
  } = props;

  const buttonClassSecondary = remove ? classes.remove : classes.secondary;

  return (
    <ButtonBase
      className={[
        primary ? classes.primary : buttonClassSecondary,
        fullWidth ? classes.fullWidth : "",
        endIcon ? "" : classes.button,
        className,
      ].join(" ")}
      disabled={disabled}
      disableRipple
      onClick={onClick}
      style={style}
      href={href}
      type={type}
    >
      {startIcon && <span className={classes.icon}>{startIcon}</span>}
      {add && <AddIcon className={classes.icon} />}
      {download && <GetAppIcon className={classes.icon} />}
      {inProgress && <CircularProgress className={classes.spinner} />}
      <span style={{ whiteSpace: noTextWrap ? "nowrap" : "normal" }}>{text}</span>
      {endIcon}
    </ButtonBase>
  );
};

export default CustomButton;
